import { gql } from '@apollo/client';

/* ── scenarios: the stress catalogue, and pricing a set of trades ── */

/**
 * The stress scenarios configured in the system. Portfolio risk metrics are
 * computed per scenario (`riskMetrics[].scenarioName`), so this is what turns
 * a scenario name into something readable — the shock it applies, whether it
 * replays a historical window, or how many standard deviations it moves.
 *
 * `scenarioValue` is the slug (`covid_19_market_crash`), and it is what every
 * backend call taking a `scenarioName` argument actually wants — the display
 * name is rejected. A row without one cannot be run against.
 */
export const STRESS_SCENARIOS = gql`
  query StressScenarios {
    allRiskStressScenarios {
      id
      scenarioName
      scenarioValue
      isActive
      description
      securitySymbol
      securityType
      isHistorical
      historicalStartDate
      historicalEndDate
      isStandardDeviation
      numberOfStandardDeviations
      shockValueNegativePercent
      shockValuePositivePercent
      shockValueNegativeAmount
      shockValuePositiveAmount
    }
  }
`;

export const SENSITIVITY_SCENARIOS = gql`
  query SensitivityScenarios {
    allRiskSensitivityScenarios {
      id
      scenarioName
      securitySymbol
      securityType
      transitivityType
      isStandardDeviation
      numberOfStandardDeviations
      shockValueNegativePercent
      shockValuePositivePercent
    }
  }
`;

/**
 * The simulation configurations a risk run can be parameterised by — the other
 * half of the scenario catalogue.
 *
 * A stress scenario says *what happens to the market*; a simulation config says
 * *how the Monte Carlo is run* (window, half-life, path count, horizon). Both
 * are addressed by a `scenarioValue` slug and both are valid values for
 * `computePortfolioRisk`'s `scenarioName`, which is why they are offered in one
 * list rather than two controls.
 *
 * Scoped to a `userTag` — the desk's own configurations plus the global ones.
 */
export const RISK_SIMULATION_CONFIGURATIONS = gql`
  query RiskSimulationConfigurations($userTag: String!) {
    riskSimulationConfigurationsByUser(userTag: $userTag) {
      id
      scenarioName
      scenarioValue
      isActive
      isGlobal
      description
      currency
      halfLifeDays
      historicWindowSize
      isExponential
      numberOfSimulations
      forecastingHorizon
    }
  }
`;

/** `urlId`: the priced scenario's handle for the agent's report — see RESULT_HEAD in queries/attribution.js. */
export const ANALYZE_SCENARIO = gql`
  query AnalyzeScenario($portfolioSymbol: String!, $trades: [ScenarioTradeInput!]!, $benchmarkSymbol: String) {
    analyzeScenario(
      portfolioSymbol: $portfolioSymbol
      trades: $trades
      includeRisk: true
      includeExposures: true
      includeBiases: false
      includeReturns: true
      benchmarkSymbol: $benchmarkSymbol
    ) {
      portfolioSymbol
      urlId
      currentTotalValue
      newTotalValue
      currentTaxLiability
      newTaxLiability
      exposureChanges {
        assetClassChanges { name currentWeight newWeight change }
        sectorChanges { name currentWeight newWeight change }
        concentrationChange
      }
      currentRisk { concentrationHerfindahl top10Concentration numberOfHoldings diversificationRatio }
      newRisk { concentrationHerfindahl top10Concentration numberOfHoldings diversificationRatio }
      riskChanges { concentrationChange holdingsCountChange diversificationChange }
      realizedPnlStcg
      realizedPnlLtcg
      totalRealizedPnl
      taxOnStcg
      taxOnLtcg
    }
  }
`;
